import { useState, type FormEvent } from 'react';
import type { AdminUser } from '@yawi/types';
import { Modal, Button, Input } from '@/core/ui/components';
import { useServices, useToast } from '@/app/providers';

interface AdminFormModalProps {
  admin?: AdminUser;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Alta y edición de staff Hommi (SUPER_ADMIN). El rol no se edita: es la única
 * opción de esta pantalla. La contraseña inicial se define aquí porque la
 * invitación por correo no existe en el backend.
 */
export function AdminFormModal({ admin, onClose, onSuccess }: AdminFormModalProps) {
  const { adminUserRepository } = useServices();
  const { showToast } = useToast();

  const isEdit = !!admin;

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: admin?.name || '',
    email: admin?.email || '',
    phone: admin?.phone || '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must have at least 2 characters';
    }

    if (!isEdit) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (formData.password.length < 8) {
        newErrors.password = 'Password must have at least 8 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const phone = formData.phone.trim() || undefined;
    setIsLoading(true);
    try {
      if (isEdit) {
        await adminUserRepository.update(admin.id, { name: formData.name.trim(), phone });
        showToast('success', 'Admin user updated successfully');
      } else {
        await adminUserRepository.create({
          name: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password,
          phone,
        });
        showToast('success', 'Admin user created. Share the initial password through a secure channel.');
      }
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} admin`;
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Admin User' : 'Add Admin User'}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isLoading}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={errors.name}
          placeholder="John Doe"
        />

        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          error={errors.email}
          placeholder="admin@example.com"
          disabled={isEdit}
        />

        <Input
          label="Phone (optional)"
          value={formData.phone}
          onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
          placeholder="+52 55 1234 5678"
        />

        {!isEdit && (
          <Input
            label="Initial password"
            type="password"
            autoComplete="new-password"
            value={formData.password}
            onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
            error={errors.password}
          />
        )}

        <p className="text-sm text-gray-500 dark:text-gray-400">
          Admin users get full Super Admin access to this panel.
        </p>
      </form>
    </Modal>
  );
}
